// Similar to ConversationListView.tsx
// Handles:
// - Vertical scrolling timeline layout
// - Event entries using TimelineEventItem components
// - Infinite scroll or pagination
// - Sorting by timestamp
// - Selection for bulk operations
// - Split view for desktop (list + detail) 

import React, { useEffect, useRef, useCallback, useState } from 'react';
import {
  Box,
  Typography,
  useTheme,
  CircularProgress,
  Button,
  alpha,
  Paper,
  Divider,
  useMediaQuery,
  List,
  Skeleton,
  Stack,
  Card,
  Fade,
  Grow,
  IconButton,
  Tooltip,
  Badge,
  Chip,
} from '@mui/material';
import { useAppDispatch, useAppSelector } from 'src/store';
import {
  selectAllEvents,
  selectSelectedEventId,
  setSelectedEvent,
  selectEventsLoading,
  selectEventsLoadingFullData,
  selectHasMoreEvents,
  selectEventFilters,
  clearFilters,
  fetchTimelineEvents,
  fetchEvents,
  fetchEventsFullData,
} from 'src/features/timeline/events.slice';
import TimelineEventItem from './TimelineEventItem';
import TimelineFilters from './TimelineFilters';
import EventDetail from './EventDetail';
import { dateTimeFormatter } from 'src/libs/dateTimeFormatter';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Event as EventIcon,
  CalendarToday as CalendarIcon,
  Refresh as RefreshIcon,
  KeyboardArrowUp as ScrollTopIcon,
  Timeline as TimelineIcon,
  AutoAwesome as AutoAwesomeIcon,
} from '@mui/icons-material';

interface TimelineListViewProps {
  selectedEventId: string | null;
  onEventSelect: (eventId: string) => void;
}

// Skeleton loading component
const EventSkeleton: React.FC = () => {
  const theme = useTheme();
  
  return (
    <Paper
      sx={{
        p: 2,
        mb: 1,
        borderRadius: 3,
        bgcolor: theme.palette.background.paper,
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'flex-start' }}>
        <Skeleton variant="circular" width={40} height={40} sx={{ mr: 1.5 }} />
        <Box sx={{ flex: 1 }}>
          <Skeleton variant="text" width="60%" height={20} sx={{ mb: 0.5 }} />
          <Stack direction="row" spacing={1} sx={{ mb: 0.5 }}>
            <Skeleton variant="text" width={80} height={16} />
            <Skeleton variant="text" width={60} height={16} />
            <Skeleton variant="text" width={70} height={16} />
          </Stack>
          <Stack direction="row" spacing={0.5}>
            <Skeleton variant="rounded" width={90} height={20} />
            <Skeleton variant="rounded" width={75} height={20} />
          </Stack>
        </Box>
      </Box>
    </Paper>
  );
};

// Date separator component
const DateSeparator: React.FC<{ date: string; eventCount: number }> = ({ date, eventCount }) => {
  const theme = useTheme();
  const darkMode = theme.palette.mode === 'dark';
  
  return (
    <motion.div
      initial={{ opacity: 0, x: -50 }}
      animate={{ opacity: 1, x: 0 }}
      transition={{ duration: 0.3 }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          my: 2,
          mx: 2,
          position: 'relative',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1.5,
            px: 2,
            py: 0.5,
            bgcolor: darkMode ? alpha(theme.palette.background.paper, 0.8) : theme.palette.background.paper,
            borderRadius: 16,
            border: `1px solid ${alpha(theme.palette.primary.main, 0.3)}`,
            boxShadow: `0 1px 4px ${alpha(theme.palette.primary.main, 0.15)}`,
            zIndex: 1,
          }}
        >
          <CalendarIcon sx={{ color: theme.palette.primary.main, fontSize: 18 }} />
          <Typography 
            variant="body2" 
            sx={{ 
              fontWeight: 600,
              color: theme.palette.text.primary,
            }}
          >
            {date}
          </Typography>
          <Tooltip title={`${eventCount} events on this date`}>
            <Chip
              label={eventCount}
              size="small"
              sx={{
                height: 20,
                fontSize: '0.75rem',
                fontWeight: 600,
                bgcolor: theme.palette.primary.main,
                color: 'white',
                '& .MuiChip-label': {
                  px: 1,
                },
              }}
            />
          </Tooltip>
        </Box>
        <Box
          sx={{
            flex: 1,
            height: 1,
            bgcolor: alpha(theme.palette.divider, 0.2),
            ml: 2,
          }}
        />
      </Box>
    </motion.div>
  );
};

const TimelineListView: React.FC<TimelineListViewProps> = ({
  selectedEventId,
  onEventSelect,
}) => {
  const dispatch = useAppDispatch();
  const theme = useTheme();
  const darkMode = theme.palette.mode === 'dark';
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const loadMoreRef = useRef<HTMLDivElement>(null);
  const isDesktop = useMediaQuery(theme.breakpoints.up('md'));
  const loading = useAppSelector(selectEventsLoading);
  const loadingFullData = useAppSelector(selectEventsLoadingFullData);
  const hasMoreEvents = useAppSelector(selectHasMoreEvents);
  const filters = useAppSelector(selectEventFilters);
  const events = useAppSelector(selectAllEvents);
  
  const listRef = useRef<HTMLDivElement>(null);
  const [showScrollTop, setShowScrollTop] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [visibleEventIds, setVisibleEventIds] = useState<Set<string>>(new Set());
  const observerRef = useRef<IntersectionObserver | null>(null);
  const visibleEventsDebounceRef = useRef<NodeJS.Timeout | null>(null);

  // Set up intersection observer to track visible events
  useEffect(() => {
    observerRef.current = new IntersectionObserver(
      (entries) => {
        // Clear any existing debounce timer
        if (visibleEventsDebounceRef.current) {
          clearTimeout(visibleEventsDebounceRef.current);
        }

        // Debounce the visible events update
        visibleEventsDebounceRef.current = setTimeout(() => {
          setVisibleEventIds((prev) => {
            const newSet = new Set(prev);
            entries.forEach((entry) => {
              const eventId = entry.target.getAttribute('data-event-id');
              if (eventId) {
                if (entry.isIntersecting) {
                  newSet.add(eventId);
                } else {
                  newSet.delete(eventId);
                }
              }
            });
            return newSet;
          });
        }, 100); // 100ms debounce
      },
      {
        root: listRef.current,
        rootMargin: '100px', // Load data for events 100px before they come into view
        threshold: 0.01,
      }
    );

    return () => {
      observerRef.current?.disconnect();
      if (visibleEventsDebounceRef.current) {
        clearTimeout(visibleEventsDebounceRef.current);
      }
    };
  }, []);

  // Load full data for visible events that don't have it yet
  useEffect(() => {
    if (visibleEventIds.size === 0 || loadingFullData) return;

    const eventsNeedingFullData = Array.from(visibleEventIds)
      .map(id => events.find(e => e.id === id))
      .filter(event => event && !event._hasFullData)
      .map(event => event!.id);

    if (eventsNeedingFullData.length > 0) {
      dispatch(fetchEventsFullData(eventsNeedingFullData));
    }
  }, [visibleEventIds, events, loadingFullData, dispatch]);

  // Ensure we fetch full data for initially visible events
  useEffect(() => {
    // Wait for initial minimal data load to complete
    if (loading || loadingFullData || events.length === 0) return;

    // Check if we have any events without full data
    const eventsWithoutFullData = events.filter(event => !event._hasFullData);
    
    // If we have events without full data and none are being observed yet (initial load)
    if (eventsWithoutFullData.length > 0 && visibleEventIds.size === 0) {
      // Take the first 10 events to load full data for
      const initialEventsToLoad = eventsWithoutFullData.slice(0, 10).map(e => e.id);
      if (initialEventsToLoad.length > 0) {
        dispatch(fetchEventsFullData(initialEventsToLoad));
      }
    }
  }, [events, loading, loadingFullData, visibleEventIds.size, dispatch]);

  const handleLoadMore = useCallback(() => {
    if (!loadingMore && hasMoreEvents && events.length > 0) {
      setLoadingMore(true);
      const lastEvent = events[events.length - 1];
      
      // Check if any filters are active
      const hasActiveFilters = Object.values(filters).some(value => value !== undefined && value !== '');
      
      if (hasActiveFilters) {
        // Use fetchEvents for full filter support - but with minimal payload
        const currentPage = Math.floor(events.length / 50) + 1;
        dispatch(fetchEvents({
          ...filters,
          page: currentPage,
          per_page: 50,
          full_payload: false, // Get minimal data first
          created_after: filters.created_after ? new Date(filters.created_after) : undefined,
          created_before: filters.created_before ? new Date(filters.created_before) : undefined,
        })).finally(() => setLoadingMore(false));
      } else {
        // Use fetchTimelineEvents for better performance when no filters
        dispatch(fetchTimelineEvents({
          before_timestamp: lastEvent.created_at,
          limit: 50,
        })).finally(() => setLoadingMore(false));
      }
    }
  }, [loadingMore, hasMoreEvents, events, dispatch, filters]);

  const handleScroll = useCallback(() => {
    if (!listRef.current) return;
    
    const { scrollTop, scrollHeight, clientHeight } = listRef.current;
    
    // Show scroll to top button when scrolled down
    setShowScrollTop(scrollTop > 300);
    
    // Check if we need to load more
    if (scrollHeight - scrollTop - clientHeight < 100 && !loadingMore && hasMoreEvents && !loading) {
      handleLoadMore();
    }
  }, [hasMoreEvents, loadingMore, loading, handleLoadMore]);

  const scrollToTop = () => {
    listRef.current?.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const renderEventsList = () => {
    // Group events by date with enhanced UI
    const eventsByDate = events.reduce((acc, event) => {
      const date = dateTimeFormatter(event.created_at, 'date_only');
      if (!acc[date]) {
        acc[date] = [];
      }
      acc[date].push(event);
      return acc;
    }, {} as Record<string, typeof events>);

    return (
      <AnimatePresence>
        {Object.entries(eventsByDate).map(([date, dateEvents], dateIndex) => (
          <motion.div
            key={date}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3, delay: dateIndex * 0.05 }}
          >
            <DateSeparator date={date} eventCount={dateEvents.length} />
            <Box sx={{ px: 2 }}>
              {dateEvents.map((event, index) => (
                <motion.div
                  key={event.id}
                  data-event-id={event.id}
                  ref={(el) => {
                    if (el && observerRef.current) {
                      observerRef.current.observe(el);
                    }
                  }}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.2, delay: index * 0.05 }}
                  style={{ marginBottom: theme.spacing(1) }}
                >
                  <TimelineEventItem
                    event={event}
                    selected={event.id === selectedEventId}
                    onClick={() => onEventSelect(event.id)}
                    isLoadingFullData={!event._hasFullData && visibleEventIds.has(event.id)}
                  />
                </motion.div>
              ))}
            </Box>
          </motion.div>
        ))}
      </AnimatePresence>
    );
  };

  const renderContent = () => {
    if (loading && events.length === 0) {
      // Initial loading state with skeletons
      return (
        <Box sx={{ p: 2 }}>
          <DateSeparator date="Loading..." eventCount={0} />
          <Stack spacing={1}>
            {[...Array(5)].map((_, index) => (
              <Fade key={index} in timeout={300 * (index + 1)}>
                <div>
                  <EventSkeleton />
                </div>
              </Fade>
            ))}
          </Stack>
        </Box>
      );
    }

    if (!loading && events.length === 0) {
      return (
        <Grow in timeout={500}>
          <Card
            sx={{
              m: 2,
              p: 3,
              textAlign: 'center',
              borderRadius: 3,
              background: darkMode
                ? `linear-gradient(135deg, ${alpha(theme.palette.background.paper, 0.9)} 0%, ${alpha(theme.palette.background.paper, 0.7)} 100%)`
                : `linear-gradient(135deg, ${alpha('#ffffff', 0.9)} 0%, ${alpha('#ffffff', 0.7)} 100%)`,
              backdropFilter: 'blur(10px)',
              border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
            }}
          >
            <TimelineIcon sx={{ fontSize: 64, color: theme.palette.action.disabled, mb: 2 }} />
            <Typography variant="h6" gutterBottom>
              No events found
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              {Object.keys(filters).length > 0 
                ? 'Try adjusting your filters to see more events'
                : 'Events will appear here as they occur'}
            </Typography>
            {Object.keys(filters).length > 0 && (
              <Button
                variant="outlined"
                onClick={() => {
                  dispatch(clearFilters());
                  dispatch(fetchEvents({ limit: 50, full_payload: false }));
                }}
                startIcon={<RefreshIcon />}
              >
                Clear Filters
              </Button>
            )}
          </Card>
        </Grow>
      );
    }

    return (
      <>
        {renderEventsList()}
        {loadingMore && (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
            <Stack spacing={1} sx={{ width: '100%' }}>
              {[...Array(3)].map((_, index) => (
                <EventSkeleton key={`loading-${index}`} />
              ))}
            </Stack>
          </Box>
        )}
        {!hasMoreEvents && events.length > 0 && (
          <Fade in timeout={500}>
            <Box sx={{ textAlign: 'center', p: 3 }}>
              <AutoAwesomeIcon sx={{ fontSize: 40, color: theme.palette.action.disabled, mb: 1 }} />
              <Typography variant="body2" color="text.secondary">
                You've reached the end of the timeline
              </Typography>
            </Box>
          </Fade>
        )}
      </>
    );
  };

  return (
    <Box
      sx={{
        width: '100%',
        height: '100%',
        display: 'flex',
        flexDirection: 'row',
        position: 'relative',
        overflow: 'hidden',
      }}
    >
      {/* Timeline List */}
      <Box
        sx={{
          width: isDesktop && selectedEventId ? '55%' : '100%',
          transition: 'width 0.3s ease',
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          borderRight: isDesktop && selectedEventId ? `1px solid ${darkMode ? 'grey.800' : 'grey.200'}` : 'none',
          overflow: 'hidden',
        }}
      >
        {/* Filters */}
        <Box sx={{ 
          p: 2, 
          borderBottom: `1px solid ${darkMode ? 'grey.800' : 'grey.200'}`,
          flexShrink: 0,
        }}>
          <TimelineFilters />
        </Box>

        {/* Timeline */}
        <Box 
          ref={listRef}
          onScroll={handleScroll}
          sx={{ 
            flex: 1, 
            overflow: 'auto',
            scrollbarWidth: 'thin',
            scrollbarColor: darkMode ? '#555 #333' : '#ccc #f0f0f0',
            '&::-webkit-scrollbar': {
              width: '8px',
            },
            '&::-webkit-scrollbar-track': {
              bgcolor: darkMode ? 'grey.800' : 'grey.100',
            },
            '&::-webkit-scrollbar-thumb': {
              bgcolor: darkMode ? 'grey.700' : 'grey.300',
              borderRadius: '4px',
              '&:hover': {
                bgcolor: darkMode ? 'grey.600' : 'grey.400',
              },
            },
          }}
        >
          {renderContent()}
        </Box>

        {/* Scroll to top button */}
        <AnimatePresence>
          {showScrollTop && (
            <motion.div
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              exit={{ scale: 0 }}
              style={{
                position: 'absolute',
                bottom: 24,
                right: isDesktop && selectedEventId ? 'calc(45% + 24px)' : '24px',
                zIndex: 10,
              }}
            >
              <Tooltip title="Scroll to top">
                <IconButton
                  onClick={scrollToTop}
                  sx={{
                    bgcolor: theme.palette.primary.main,
                    color: 'white',
                    boxShadow: 4,
                    '&:hover': {
                      bgcolor: theme.palette.primary.dark,
                    },
                  }}
                >
                  <ScrollTopIcon />
                </IconButton>
              </Tooltip>
            </motion.div>
          )}
        </AnimatePresence>
      </Box>

      {/* Desktop split view - Event Detail */}
      {isDesktop && selectedEventId && (
        <Box
          sx={{
            width: '45%',
            height: '100%',
            bgcolor: darkMode ? 'grey.900' : 'background.paper',
            overflow: 'auto',
            flexShrink: 0,
          }}
        >
          <EventDetail eventId={selectedEventId} />
        </Box>
      )}
    </Box>
  );
};

export default TimelineListView; 
